import { useState, useCallback, useEffect } from "react";
import { Box, Button, Dialog, DialogContent, DialogActions, Stack, Snackbar, Alert } from "@mui/material";
import Cropper from "react-easy-crop";
import IAlert from "../../types/Alert";

interface ImageUploaderProps {
    aspect?: number;
    onCropped: (croppedImage: string | null) => void;
    label?: string;
    initialImage?: string | null;
    size?: number;
}

// ---------- LÍMITES ----------
const MAX_INPUT_FILE_SIZE = 2 * 1024 * 1024; // 2 MB
const MAX_INPUT_DIMENSION = 2000; // px, lado máximo de la imagen de entrada
const MAX_OUTPUT_DIMENSION = 460; // px, lado mayor de la imagen de salida (tras crop)
const OUTPUT_JPEG_QUALITY = 0.8;
const ALLOWED_MIME_TYPES = ["image/png", "image/jpeg"];

const ImageUploader = ({
    aspect = 1,
    onCropped,
    label = "Subir imagen",
    initialImage = null,
    size = 80
}: ImageUploaderProps) => {
    const [alert, setAlert] = useState<IAlert>({ open: false, severity: 'info', msg: '' });
    const [imgSrc, setImgSrc] = useState<string | null>(null);
    const [imgMimeType, setImgMimeType] = useState<string>("image/png");
    const [showDialog, setShowDialog] = useState(false);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState<any>(null);
    const [croppedImage, setCroppedImage] = useState<string | null>(initialImage);

    const handleCloseAlert = (_?: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === 'clickaway') {
            return;
        }
        setAlert({ open: false, severity: 'info', msg: '' });
    };

    useEffect(() => {
        if (initialImage) {
            setCroppedImage(initialImage);
        }
    }, [initialImage]);

    const readFile = (file: File) =>
        new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.addEventListener("load", () => resolve(reader.result as string));
            reader.readAsDataURL(file);
        });

    const createImage = (url: string) =>
        new Promise<HTMLImageElement>((resolve, reject) => {
            const image = new Image();
            image.addEventListener("load", () => resolve(image));
            image.addEventListener("error", (error) => reject(error));
            image.src = url;
        });
    
    const getOutputDimensions = (aspectRatio: number) => {
        if (aspectRatio >= 1) {
            const width = MAX_OUTPUT_DIMENSION;
            const height = Math.round(MAX_OUTPUT_DIMENSION / aspectRatio);
            return { width, height };
        } else {
            const height = MAX_OUTPUT_DIMENSION;
            const width = Math.round(MAX_OUTPUT_DIMENSION * aspectRatio);
            return { width, height };
        }
    };

    const getCroppedImg = async (imageSrc: string, pixelCrop: any, mimeType: string) => {
        const image = await createImage(imageSrc);
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        if (!ctx) return null;

        const { width: outWidth, height: outHeight } = getOutputDimensions(
            pixelCrop.width / pixelCrop.height
        );

        canvas.width = outWidth;
        canvas.height = outHeight;

        ctx.clearRect(0, 0, canvas.width, canvas.height);

        ctx.drawImage(
            image,
            pixelCrop.x,
            pixelCrop.y,
            pixelCrop.width,
            pixelCrop.height,
            0,
            0,
            outWidth,
            outHeight
        );

        const isPng = mimeType === "image/png";

        return canvas.toDataURL(isPng ? "image/png" : "image/jpeg", isPng ? undefined : OUTPUT_JPEG_QUALITY);
    };

    const onCropComplete = useCallback((_: any, croppedAreaPixels: any) => {
        setCroppedAreaPixels(croppedAreaPixels);
    }, []);

    const handleConfirm = useCallback(async () => {
        if (!imgSrc || !croppedAreaPixels) return;
        const cropped = await getCroppedImg(imgSrc, croppedAreaPixels, imgMimeType);
        if (cropped) {
            setCroppedImage(cropped);
            onCropped(cropped);
            setShowDialog(false);
        }
    }, [imgSrc, croppedAreaPixels, onCropped, imgMimeType]);

    const onFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            const file = e.target.files[0];

            if (!ALLOWED_MIME_TYPES.includes(file.type)) {
                setAlert({ open: true, severity: 'error', msg: 'Tipo de archivo no permitido. Solo se permiten imágenes PNG y JPEG.' });
                e.target.value = "";
                return;
            }

            if (file.size > MAX_INPUT_FILE_SIZE) {
                setAlert({ open: true, severity: 'error', msg: 'El archivo es demasiado grande. El tamaño máximo permitido es 5MB.' });
                e.target.value = "";
                return;
            }

            const imageDataUrl = await readFile(file);

             const image = await createImage(imageDataUrl);
            if (image.naturalWidth > MAX_INPUT_DIMENSION || image.naturalHeight > MAX_INPUT_DIMENSION) {
                e.target.value = "";
                return;
            }

            setImgMimeType(file.type);
            setImgSrc(imageDataUrl);
            setShowDialog(true);
            e.target.value = "";
        }
    };

    const handleRemove = () => {
        setCroppedImage(null);
        onCropped(null);
    };

    return (
        <>
        <Stack direction="column" spacing={2} alignItems="center">
            {croppedImage && (
                <Box sx={{ width: size, height: size / aspect, borderRadius: aspect === 1 ? "50%" : "8px", overflow: "hidden", boxShadow: "0 0 4px rgba(0,0,0,0.2)" }}>
                    <img
                        src={croppedImage}
                        alt="Cropped"
                        style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover"
                        }}
                    />
                </Box>
            )}
        
            <Stack direction="row" spacing={1} alignItems="center">
                <Button variant="contained" component="label">
                    {croppedImage ? "Editar" : label}
                    <input hidden accept="image/*" type="file" onChange={onFileChange} />
                </Button>
        
                {croppedImage && (
                    <Button variant="outlined" color="error" onClick={handleRemove}>
                        Eliminar
                    </Button>
                )}
            </Stack>
            
            <Dialog open={showDialog} onClose={() => setShowDialog(false)} maxWidth="sm" fullWidth>
                <DialogContent sx={{ position: "relative", height: 400, background: "#333" }}>
                    {imgSrc && (
                        <Cropper
                            image={imgSrc}
                            crop={crop}
                            zoom={zoom}
                            aspect={aspect}
                            onCropChange={setCrop}
                            onZoomChange={setZoom}
                            onCropComplete={onCropComplete}
                        />
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowDialog(false)}>Cancelar</Button>
                    <Button variant="contained" onClick={handleConfirm}>
                        Aceptar
                    </Button>
                </DialogActions>
            </Dialog>
        </Stack>
        <Snackbar open={alert.open} autoHideDuration={6000} onClose={handleCloseAlert}>
            <Alert variant="filled" onClose={handleCloseAlert} severity={alert.severity} sx={{ width: '100%' }}>{alert.msg}</Alert>
        </Snackbar>
        </>
    );
};

export default ImageUploader;
